import { useCallback, useContext, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { useStyles } from "./WeekTrend.styles";
import { useTranslation } from "react-i18next";
import { LineChart } from "../LineChart/LineChart.view";
import { MillsEntity } from "entities/Mills/MillsEntity";
import { useParams } from "react-router-dom";
import { ILineChartData, IWeekTrend, IWeekTrendAxes } from "./WeekTrendInterfaces";
import { IEventFailureCodeSummaryPerMC } from "entities/Mills/MillsEntityInterfaces";
import { getTimeTrends } from "utilities/utilities";
import { LineChartsLegends } from "../LineChartLegends/LineChartLegends.view";
import NoData from "components/NoData/NoData.view";
import moment from "moment";
import { SummaryReportDashboardContext } from "contexts/SummaryReportDashboardContext/SummaryReportDashboardContext";
import { initialMillFilters } from "pages/Corporate/Locations/MillDashboards/SummaryReport/components/SummaryReportFilters/useMillFilters";

export const WeekTrend = () => {
    const classes = useStyles();
    const { t } = useTranslation();
    const { millId } = useParams();
    const { filters } = useContext(SummaryReportDashboardContext);
    const queryFilters = useMemo(
        () => ({
            ...filters,
            startDate: filters.startDate ? filters.startDate : initialMillFilters.startDate,
            endDate: filters.endDate ? filters.endDate : initialMillFilters.endDate,
        }),
        [filters],
    );
    const { failureTypes13WeekTrend } = MillsEntity.GetFailureTypes13WeekTrend(+millId!, queryFilters);

    const thirteenWeeksFromNow = useMemo(
        () => (queryFilters.endDate ? getTimeTrends(queryFilters.endDate, 13, 7) : []),
        [queryFilters.endDate],
    );

    const getFailureTypeTrendDate = useCallback(
        (date: Date) => {
            for (let i = 0; i < thirteenWeeksFromNow.length; i++) {
                if (date < thirteenWeeksFromNow[i] && date > thirteenWeeksFromNow[i - 1]) {
                    return thirteenWeeksFromNow[i].toISOString();
                }
            }
        },
        [thirteenWeeksFromNow],
    );

    const statsByFailureType: { [key: string]: IWeekTrend } = useMemo(() => {
        const statsObject: { [key: string]: IWeekTrend } = {};
        failureTypes13WeekTrend.forEach((stat: IEventFailureCodeSummaryPerMC) => {
            if (Object.keys(statsObject).includes(stat.failure_type.name)) {
                statsObject[stat.failure_type.name].data.push({
                    x: getFailureTypeTrendDate(new Date(moment(new Date(stat.created_at)).format("YYYY-MM-DD")))!,
                    y: stat.occurrences,
                });
            } else {
                const values: IWeekTrend = {
                    name: stat.failure_type.name,
                    color: stat.failure_type.color_code,
                    data: [
                        {
                            x: getFailureTypeTrendDate(
                                new Date(moment(new Date(stat.created_at)).format("YYYY-MM-DD")),
                            )!,
                            y: stat.occurrences,
                        },
                    ],
                };

                statsObject[values.name] = values;
            }
        });

        return statsObject;
    }, [failureTypes13WeekTrend, getFailureTypeTrendDate]);

    useMemo(() => {
        for (const item in statsByFailureType) {
            const failureTypesStatesDates: string[] = statsByFailureType[item].data.map((dataItem) => dataItem.x);
            thirteenWeeksFromNow.forEach((date: Date) => {
                if (!failureTypesStatesDates.includes(new Date(date).toISOString())) {
                    statsByFailureType[item].data.push({
                        x: new Date(date).toISOString(),
                        y: 0,
                    });
                }
            });

            statsByFailureType[item].data.sort((a, b) => Date.parse(a.x) - Date.parse(b.x));
        }
    }, [thirteenWeeksFromNow, statsByFailureType]);

    const lineChartMemoized = useMemo(() => {
        const lineChartData: ILineChartData[] = [];

        for (const key in statsByFailureType) {
            let summarizedOccurencesByDate: { [key: string]: number } = {};
            statsByFailureType[key].data.forEach((item) => {
                if (summarizedOccurencesByDate[item.x]) {
                    summarizedOccurencesByDate[item.x] += item.y;
                } else {
                    summarizedOccurencesByDate[item.x] = item.y;
                }
            });
            let summarizedOccurencesByDateArray: IWeekTrendAxes[] = [];
            for (const keyDate in summarizedOccurencesByDate) {
                summarizedOccurencesByDateArray = [
                    ...summarizedOccurencesByDateArray,
                    { x: keyDate, y: summarizedOccurencesByDate[keyDate] },
                ];
            }

            const item = {
                id: statsByFailureType[key].name,
                color: statsByFailureType[key].color,
                data: summarizedOccurencesByDateArray,
            };

            lineChartData.push(item);
        }
        return lineChartData;
    }, [statsByFailureType]);

    return (
        <Box className={classes.container} sx={{ flexDirection: "column" }}>
            <Box className={classes.titleContainer}>
                <Typography className={classes.title} variant="subtitle1">
                    {t("locations.summary_report.week_trend.title", { amount: "13" })}
                </Typography>
            </Box>

            {!lineChartMemoized.length ? (
                <Box className={classes.noData}>
                    <NoData />
                </Box>
            ) : (
                <Box className={classes.graphContainer}>
                    <Box className={classes.graphGridItemContainer}>
                        <LineChart data={lineChartMemoized} />
                    </Box>
                    <LineChartsLegends data={lineChartMemoized} />
                </Box>
            )}
        </Box>
    );
};
